(() => {
  "use strict";

  if (window.GuruGlobalNavigation?.init) {
    window.GuruGlobalNavigation.init(document);
    return;
  }

  const ROOT_SELECTOR = "[data-guru-navigation]";
  const EXTERNAL_MARKET_TRIGGER = "[data-guru-market-open]";
  const OPEN_EVENT = "guru:overlay:open";
  const CLOSE_EVENT = "guru:overlay:close";
  const PREDICTIVE_INVALIDATE_EVENT = "guru:predictive-search:invalidate";
  const transitionDuration = () => (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 220
  );
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "summary",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const instances = new Map();

  function openRequestedCart() {
    const url = new URL(window.location.href);
    if (url.searchParams.get("guru_cart") !== "open") return;

    url.searchParams.delete("guru_cart");
    window.history.replaceState(
      null,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );

    const open = () => {
      const drawer = document.getElementById("cart-drawer");
      if (!drawer) return;
      const trigger = document.querySelector("[data-guru-cart-trigger]");
      if (typeof drawer.show === "function") drawer.show(trigger);
      else drawer.setAttribute("open", "immediate");
    };

    if (window.customElements.get("cart-drawer")) {
      window.requestAnimationFrame(open);
    } else {
      void window.customElements.whenDefined("cart-drawer").then(open);
    }
  }

  function isVisible(element) {
    return Boolean(
      element
      && element.isConnected
      && !element.closest("[hidden]")
      && element.getClientRects().length,
    );
  }

  function primaryInstance() {
    return [...instances.values()].find((instance) => isVisible(instance.root))
      || [...instances.values()][0]
      || null;
  }

  function translationsFor(root) {
    try {
      const value = JSON.parse(
        root.querySelector("[data-guru-navigation-translations]")?.textContent || "{}",
      );
      return value && typeof value === "object" ? value : {};
    } catch (_error) {
      return {};
    }
  }

  function sentenceCase(value, locale) {
    const text = String(value || "").trim();
    const [first = "", ...rest] = Array.from(text);
    return `${first.toLocaleUpperCase(locale || undefined)}${rest.join("")}`;
  }

  class GuruGlobalNavigation {
    constructor(root) {
      this.root = root;
      this.section = root.closest(".shopify-section");
      this.header = root.querySelector("[data-guru-header-bar]");
      this.panels = new Map(
        [...root.querySelectorAll("[data-guru-panel]")]
          .map((panel) => [panel.dataset.guruPanel, panel]),
      );
      this.activeName = null;
      this.returnFocus = null;
      this.hideTimers = new Map();
      this.inertedElements = new Map();
      this.backgroundObserver = "MutationObserver" in window
        ? new MutationObserver(() => {
          if (this.activeName) this.refreshBackgroundInert();
        })
        : null;
      this.scrollLockState = null;
      this.listeners = new AbortController();
      this.searchRequest = null;
      this.searchSequence = 0;
      this.searchTimer = null;
      this.searchNeedsRefresh = false;
      this.activePillar = null;
      this.pillarCloseTimer = null;
      this.pillarHideTimer = null;
      this.geometryFrame = null;
      this.scrollAnchor = window.scrollY;
      this.geometryOwners = new Set();
      this.resizeObserver = null;
      this.stickyToolbar = null;
      this.cartDrawer = null;
      this.translations = translationsFor(root);

      this.onRootClick = this.onRootClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onNativeCartOpen = this.onNativeCartOpen.bind(this);

      this.mount();
    }

    translate(key, replacements = {}) {
      let value = typeof this.translations[key] === "string" ? this.translations[key] : "";
      Object.entries(replacements).forEach(([token, replacement]) => {
        value = value.replaceAll(`__${token.toUpperCase()}__`, String(replacement));
      });
      return value;
    }

    mount() {
      const signal = this.listeners.signal;

      this.root.addEventListener("click", this.onRootClick, { signal });
      document.addEventListener("keydown", this.onKeyDown, { signal });
      document.addEventListener("focusin", this.onFocusIn, { signal, capture: true });
      window.addEventListener("resize", () => this.scheduleGeometryUpdate(), { signal, passive: true });
      window.addEventListener("scroll", () => this.scheduleGeometryUpdate(), { signal, passive: true });

      if ("ResizeObserver" in window && this.header) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleGeometryUpdate());
        this.resizeObserver.observe(this.header);
      }

      this.root.querySelector('[data-guru-open="menu"]')?.addEventListener("pointerenter", (event) => {
        if (event.pointerType === "mouse" && window.matchMedia("(min-width:768px) and (hover: hover) and (pointer: fine)").matches) this.open("menu", event.currentTarget);
      }, { signal });
      const menuDrawer = this.panels.get("menu")?.querySelector(".mobile-drawer");
      menuDrawer?.addEventListener("pointerleave", (event) => {
        if (event.pointerType !== "mouse" || !window.matchMedia("(min-width:768px) and (hover:hover)").matches) return;
        if (this.fallbackTrigger("menu")?.contains(event.relatedTarget)) return;
        window.clearTimeout(this.menuLeaveTimer);
        this.menuLeaveTimer = window.setTimeout(() => { if (this.activeName === "menu") this.close(); }, 120);
      }, { signal });
      menuDrawer?.addEventListener("pointerenter", () => window.clearTimeout(this.menuLeaveTimer), { signal });
      this.setupSearch();
      this.setupPillarNavigation();
      this.setupCategoryNavigation();
      this.bindNativeCart();
      this.syncExternalMarketTriggers();
      this.syncCartCount();
      this.updateGeometry();
    }

    scheduleGeometryUpdate() {
      if (this.geometryFrame !== null) return;
      this.geometryFrame = window.requestAnimationFrame(() => {
        this.geometryFrame = null;
        this.updateGeometry();
      });
    }

    geometryOwnerSections(scope = document) {
      const sections = [];
      if (scope?.matches?.(".shopify-section")) sections.push(scope);
      scope?.querySelectorAll?.(".shopify-section").forEach((section) => sections.push(section));
      return sections;
    }

    isUpstreamGeometryOwner(section) {
      if (
        !(section instanceof HTMLElement)
        || !section.isConnected
        || !this.section?.isConnected
        || section === this.section
        || section.contains(this.section)
        || this.section.contains(section)
      ) return false;

      return Boolean(
        section.compareDocumentPosition(this.section) & Node.DOCUMENT_POSITION_FOLLOWING
      );
    }

    observeGeometryOwners(scope = document) {
      if (!this.activeName || !this.resizeObserver) return;

      this.geometryOwnerSections(scope).forEach((section) => {
        if (!this.isUpstreamGeometryOwner(section) || this.geometryOwners.has(section)) return;
        this.geometryOwners.add(section);
        this.resizeObserver.observe(section);
      });
    }

    releaseGeometryOwners(scope = null) {
      this.geometryOwners.forEach((section) => {
        if (scope && section !== scope && !scope.contains?.(section)) return;
        this.resizeObserver?.unobserve(section);
        this.geometryOwners.delete(section);
      });
    }

    bindNativeCart() {
      const cartDrawer = document.getElementById("cart-drawer");
      if (!cartDrawer || cartDrawer === this.cartDrawer) return;

      this.cartDrawer?.removeEventListener("dialog:before-show", this.onNativeCartOpen);
      this.cartDrawer = cartDrawer;
      this.cartDrawer.addEventListener("dialog:before-show", this.onNativeCartOpen, {
        signal: this.listeners.signal,
      });

      const cartCount = this.root.querySelector("cart-count");
      if (cartCount && "MutationObserver" in window) {
        this.cartCountObserver?.disconnect();
        this.cartCountObserver = new MutationObserver(() => this.syncCartCount());
        this.cartCountObserver.observe(cartCount, { childList: true, characterData: true, subtree: true });
      }
    }

    onNativeCartOpen() {
      this.close({ restoreFocus: false, immediate: true });
      this.closePillar({ immediate: true });
      this.closeAccountMenu();
    }

    syncCartCount() {
      const countElement = this.root.querySelector("cart-count");
      const trigger = this.root.querySelector("[data-guru-cart-trigger]");
      const count = Number.parseInt(countElement?.textContent?.trim() || "0", 10) || 0;

      if (trigger) {
        trigger.setAttribute(
          "aria-label",
          this.translate(count === 1 ? "cartOne" : "cartOther", { count }),
        );
      }
    }

    updateGeometry() {
      if (!this.header) return;

      const headerRect = this.header.getBoundingClientRect();
      const headerHeight = headerRect.height;
      const viewportHeight = Math.max(0, window.innerHeight);
      const overlayTop = Math.max(0, Math.min(viewportHeight, headerRect.top));
      const headerBottom = Math.max(0, Math.min(viewportHeight, headerRect.bottom));
      document.documentElement.style.setProperty("--header-height", `${headerHeight.toFixed(2)}px`);
      this.root.style.setProperty("--guru-navigation-overlay-top", `${overlayTop.toFixed(2)}px`);
      this.root.style.setProperty("--guru-navigation-header-bottom", `${headerBottom.toFixed(2)}px`);
      this.syncStickyVisualState(headerRect);
      if (this.activeName === "menu") this.refreshBackgroundInert();


      const searchShell = this.panels.get("search");
      const searchPanel = searchShell?.querySelector(".search-panel");
      if (searchShell && searchPanel) {
        const searchTop = window.matchMedia("(max-width: 959px)").matches ? overlayTop : headerBottom;
        searchShell.style.top = `${searchTop.toFixed(2)}px`;
        searchPanel.style.maxHeight = `${Math.max(0, viewportHeight - searchTop).toFixed(2)}px`;
      }
    }

    syncStickyVisualState(headerRect) {
      const sectionStyle = this.section ? window.getComputedStyle(this.section) : null;
      const sectionRect = this.section?.getBoundingClientRect();
      const parsedStickyTop = Number.parseFloat(sectionStyle?.top || "0");
      const stickyTop = Number.isFinite(parsedStickyTop) ? parsedStickyTop : 0;
      const scrollTop = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const isStuck = Boolean(
        this.root.dataset.sticky === "true"
        && sectionStyle?.position === "sticky"
        && sectionRect
        && scrollTop > 1
        && sectionRect.top <= stickyTop + 1
      );

      const mobile = window.matchMedia("(max-width: 959px)").matches;
      if (!mobile || scrollTop <= headerRect.height || Math.abs(scrollTop - this.scrollAnchor) >= 8) {
        this.root.dataset.mobileHeader = mobile && this.header.querySelector(".header-search") && this.root.dataset.sticky === "true" && scrollTop > headerRect.height && scrollTop > this.scrollAnchor ? "compact" : "full";
        this.scrollAnchor = scrollTop;
      }

      this.root.dataset.scrollState = isStuck ? "stuck" : "top";

      const nextToolbar = this.root.ownerDocument.querySelector(
        ".guru-collection__toolbar, .guru-search__toolbar",
      );
      if (this.stickyToolbar && this.stickyToolbar !== nextToolbar) {
        this.stickyToolbar.removeAttribute("data-guru-sticky-stack");
      }
      this.stickyToolbar = nextToolbar instanceof HTMLElement ? nextToolbar : null;

      const toolbarStyle = this.stickyToolbar
        ? window.getComputedStyle(this.stickyToolbar)
        : null;
      const toolbarRect = this.stickyToolbar?.getBoundingClientRect();
      const isAttached = Boolean(
        isStuck
        && toolbarStyle?.position === "sticky"
        && toolbarRect
        && Math.abs(toolbarRect.top - headerRect.bottom) <= 2
        && toolbarRect.bottom > headerRect.bottom
      );

      this.root.dataset.stickyStack = isAttached ? "attached" : "none";
      if (isAttached) this.stickyToolbar?.setAttribute("data-guru-sticky-stack", "attached");
      else this.stickyToolbar?.removeAttribute("data-guru-sticky-stack");
    }

    onRootClick(event) {
      const queryTrigger = event.target.closest("[data-guru-search-query]");
      if (queryTrigger && this.root.contains(queryTrigger) && this.search) {
        event.preventDefault();
        this.invalidatePredictiveQuickBuy("suggestion");
        const query = (queryTrigger.dataset.guruSearchQuery || "").trim();
        if (!query) return;
        const searchUrl = new URL(this.root.dataset.searchUrl || "/search", window.location.origin);
        searchUrl.searchParams.set("q", query.toLocaleLowerCase(this.root.dataset.language || undefined));
        searchUrl.searchParams.set("type", "product");
        this.close({ restoreFocus: false, immediate: true });
        window.location.assign(searchUrl.href);
        return;
      }

      const searchNavigation = event.target.closest('[data-guru-panel="search"] a[href]');
      if (
        searchNavigation
        && this.root.contains(searchNavigation)
        && !searchNavigation.matches("[data-guru-product-card-action][aria-controls]")
      ) this.invalidatePredictiveQuickBuy("navigation");

      const pillarNavigation = event.target.closest("[data-guru-pillar-panel] a[href]");
      if (pillarNavigation && this.root.contains(pillarNavigation)) {
        this.closePillar({ immediate: true });
      }

      const accountTrigger = event.target.closest("[data-min-guru-account-trigger]");
      if (accountTrigger && this.root.contains(accountTrigger)) {
        this.closePillar({ immediate: true });
      }

      const openTrigger = event.target.closest("[data-guru-open]");
      if (openTrigger && this.root.contains(openTrigger)) {
        event.preventDefault();
        if (openTrigger.dataset.guruOpen === "menu" && this.activeName === "menu") this.close();
        else this.open(openTrigger.dataset.guruOpen, openTrigger);
        return;
      }

      const closeTrigger = event.target.closest("[data-guru-close]");
      if (closeTrigger && this.root.contains(closeTrigger)) {
        event.preventDefault();
        this.close();
        return;
      }

      const cartTrigger = event.target.closest("[data-guru-cart-trigger]");
      if (cartTrigger && this.root.contains(cartTrigger)) {
        this.close({ restoreFocus: false, immediate: true });
        this.closePillar({ immediate: true });
        this.closeAccountMenu();
        return;
      }

      const menuNavigation = event.target.closest('[data-guru-panel="menu"] a[href]');
      if (menuNavigation && this.root.contains(menuNavigation)) {
        this.close({ restoreFocus: false, immediate: true });
      }
    }

    open(name, trigger = document.activeElement) {
      window.clearTimeout(this.menuLeaveTimer);
      const panel = this.panels.get(name);
      if (!panel) return false;

      if (name === "search") this.hydrateSearchRecommendations();

      const previousReturnFocus = this.activeName ? this.returnFocus : null;
      if (this.activeName === name) {
        this.focusInitial(name, panel);
        return true;
      }

      [...instances.values()].forEach((instance) => {
        if (instance !== this) {
          instance.close({ restoreFocus: false, immediate: true });
          instance.closePillar({ immediate: true });
        }
      });

      this.closePillar({ immediate: true });
      this.close({ restoreFocus: false, immediate: true });
      this.hideNativeCart();
      this.closeAccountMenu();
      this.returnFocus = previousReturnFocus || (isVisible(trigger) ? trigger : this.fallbackTrigger(name));
      this.activeName = name;
      this.observeGeometryOwners();
      this.clearHideTimer(name);
      panel.hidden = false;
      panel.setAttribute("aria-hidden", "false");
      if (name === "menu" || name === "market") panel.getBoundingClientRect();
      panel.classList.add("is-open");
      this.root.classList.add("nav-overlay-open");
      this.syncExpanded(name, true);
      this.setBackgroundInert(true);
      this.lockScroll();
      this.updateGeometry();

      if (name === "search") this.focusInitial(name, panel);

      if (name === "search" && this.searchNeedsRefresh) {
        const query = this.search?.input.value.trim() || "";
        this.searchNeedsRefresh = false;
        if (query.length >= 2) void this.fetchSearch(query);
        else this.resetSearch();
      }

      window.requestAnimationFrame(() => this.focusInitial(name, panel));
      document.dispatchEvent(new CustomEvent("guru:overlay:change", {
        detail: { name, open: true, root: this.root },
      }));
      return true;
    }

    close({
      restoreFocus = true,
      immediate = false,
      preservePredictiveQuickBuy = false,
    } = {}) {
      window.clearTimeout(this.menuLeaveTimer);
      if (!this.activeName) return false;

      const name = this.activeName;
      const panel = this.panels.get(name);
      const focusTarget = this.returnFocus;
      if (name === "search" && !preservePredictiveQuickBuy) {
        this.invalidatePredictiveQuickBuy("close");
      }
      const pendingSearch = name === "search" && Boolean(
        this.searchTimer !== null
        || this.search?.panel.dataset.searchState === "loading"
      );
      this.activeName = null;
      this.returnFocus = null;
      if (pendingSearch) {
        const query = this.search?.input.value.trim() || "";
        this.resetSearch({ invalidateQuickBuy: !preservePredictiveQuickBuy });
        this.searchNeedsRefresh = query.length >= 2;
      } else {
        this.searchRequest?.abort();
        this.searchRequest = null;
        window.clearTimeout(this.searchTimer);
        this.searchTimer = null;
      }
      panel?.classList.remove("is-open");
      panel?.setAttribute("aria-hidden", "true");
      this.root.classList.remove("nav-overlay-open");
      this.syncExpanded(name, false);
      this.setBackgroundInert(false);
      this.releaseGeometryOwners();
      this.unlockScroll();

      if (panel) {
        const delay = immediate ? 0 : (name === "menu" || name === "market") && window.matchMedia("(min-width:768px)").matches && !window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 300 : transitionDuration();
        const timer = window.setTimeout(() => {
          if (this.activeName !== name) {
            panel.hidden = true;
            if (name === "menu") {
              panel.querySelectorAll("[data-menu-category][open]").forEach((item) => { item.open = false; });
              panel.querySelector(".mobile-drawer")?.classList.remove("has-category");
            }
          }
          this.hideTimers.delete(name);
        }, delay);
        this.hideTimers.set(name, timer);
      }

      if (restoreFocus) {
        const target = isVisible(focusTarget) ? focusTarget : this.fallbackTrigger(name);
        target?.focus({ preventScroll: true });
      }

      document.dispatchEvent(new CustomEvent("guru:overlay:change", {
        detail: { name, open: false, root: this.root },
      }));
      return true;
    }

    hideNativeCart() {
      this.bindNativeCart();
      if (!this.cartDrawer?.hasAttribute("open")) return;

      if (typeof this.cartDrawer.hide === "function") {
        this.cartDrawer.hide();
      } else {
        this.cartDrawer.removeAttribute("open");
      }
    }

    closeAccountMenu() {
      this.root.querySelectorAll("details[data-min-guru-account-menu][open]").forEach((details) => {
        details.open = false;
      });
    }

    syncExpanded(name, expanded) {
      this.root.querySelectorAll(`[data-guru-open="${name}"]`).forEach((trigger) => {
        trigger.setAttribute("aria-expanded", String(expanded));
        if (name === "menu") {
          trigger.setAttribute(
            "aria-label",
            this.translate(expanded ? "closeMenu" : "openMenu"),
          );
        }
      });

      if (name === "market") {
        this.syncExternalMarketTriggers(expanded);
      }
    }

    syncExternalMarketTriggers(expanded = this.activeName === "market") {
      const marketPanelId = this.panels.get("market")?.id;
      document.querySelectorAll(EXTERNAL_MARKET_TRIGGER).forEach((trigger) => {
        trigger.setAttribute("aria-expanded", String(expanded));
        trigger.setAttribute("aria-haspopup", "dialog");
        if (marketPanelId) trigger.setAttribute("aria-controls", marketPanelId);
      });
    }

    fallbackTrigger(name) {
      return [...this.root.querySelectorAll(`[data-guru-open="${name}"]`)].find(isVisible)
        || this.root.querySelector("[data-guru-open='menu']")
        || null;
    }

    focusInitial(name, panel) {
      if (this.activeName !== name) return;

      const target = name === "search"
        ? panel.querySelector("[data-guru-search-input]")
        : this.getFocusable(panel)[0];
      (target || panel.querySelector("[role='dialog']"))?.focus({ preventScroll: true });
    }

    getFocusable(panel) {
      return [...panel.querySelectorAll(FOCUSABLE_SELECTOR)]
        .filter((element) => element.tabIndex >= 0 && isVisible(element));
    }

    desktopMenuControls() {
      if (this.activeName !== "menu" || !window.matchMedia("(min-width:768px)").matches) return [];
      return [this.fallbackTrigger("menu"), ...this.root.querySelectorAll("[data-guru-pillar-trigger]")]
        .filter((element) => element && isVisible(element));
    }

    onKeyDown(event) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        const searchPanel = this.panels.get("search");
        if (!searchPanel) return;

        event.preventDefault();
        if (this.activeName === "search") this.close();
        else this.open("search", this.root.querySelector("[data-guru-open='search']"));
        return;
      }

      if (event.key === "Escape" && this.activePillar) {
        event.preventDefault();
        this.closePillar({ restoreFocus: true });
        return;
      }

      if (!this.activeName) return;
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== "Tab") return;
      const panel = this.panels.get(this.activeName);
      const focusable = this.getFocusable(panel);
      focusable.unshift(...this.desktopMenuControls());
      if (!focusable.length) {
        event.preventDefault();
        panel.querySelector("[role='dialog']")?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFocusIn(event) {
      if (!this.activeName) return;

      const panel = this.panels.get(this.activeName);
      if (!panel || panel.contains(event.target) || this.desktopMenuControls().some((control) => control.contains(event.target))) return;

      event.stopPropagation();
      this.focusInitial(this.activeName, panel);
    }

    backgroundElements(panel) {
      const elements = new Set();
      const controls = this.desktopMenuControls();
      const addBackground = (element) => {
        if (controls.includes(element)) return;
        if (controls.some((control) => element.contains(control))) [...element.children].forEach(addBackground);
        else if (element instanceof HTMLElement) elements.add(element);
      };
      let branch = panel;

      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        [...parent.children].forEach((sibling) => {
          if (
            sibling !== branch
            && sibling instanceof HTMLElement
            && !sibling.matches("script, style, link, meta, template, noscript")
          ) addBackground(sibling);
        });
        branch = parent;
      }

      return elements;
    }

    restoreBackgroundElement(element, state) {
      if (state.hadInert) element.setAttribute("inert", state.inertValue);
      else element.removeAttribute("inert");
    }

    refreshBackgroundInert() {
      const panel = this.panels.get(this.activeName);
      if (!panel) return;

      const background = this.backgroundElements(panel);
      this.inertedElements.forEach((state, element) => {
        if (background.has(element)) return;
        this.restoreBackgroundElement(element, state);
        this.inertedElements.delete(element);
      });

      background.forEach((element) => {
        if (!this.inertedElements.has(element)) {
          this.inertedElements.set(element, {
            hadInert: element.hasAttribute("inert"),
            inertValue: element.getAttribute("inert") || "",
          });
        }
        if (!element.hasAttribute("inert")) element.setAttribute("inert", "");
      });
    }

    setBackgroundInert(inert) {
      if (inert) {
        this.refreshBackgroundInert();
        this.backgroundObserver?.disconnect();
        this.backgroundObserver?.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["inert"],
        });
        return;
      }

      this.backgroundObserver?.disconnect();
      this.inertedElements.forEach((state, element) => {
        this.restoreBackgroundElement(element, state);
      });
      this.inertedElements.clear();
    }

    syncAfterSectionLoad(scope) {
      if (this.activeName) {
        this.setBackgroundInert(true);
        this.observeGeometryOwners(scope);
      }
      this.scheduleGeometryUpdate();
    }

    syncBeforeSectionUnload(scope) {
      this.releaseGeometryOwners(scope);
      if (this.activeName) this.scheduleGeometryUpdate();
    }

    lockScroll() {
      if (this.scrollLockState) return;

      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      this.scrollLockState = {
        htmlOverflow: document.documentElement.style.overflow,
        bodyOverflow: document.body.style.overflow,
        bodyPaddingRight: document.body.style.paddingRight,
        hadClass: document.documentElement.classList.contains("guru-nav-lock"),
      };
      document.documentElement.classList.add("guru-nav-lock");
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      if (scrollbarWidth > 0) document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    unlockScroll() {
      if (!this.scrollLockState) return;

      const state = this.scrollLockState;
      document.documentElement.style.overflow = state.htmlOverflow;
      document.body.style.overflow = state.bodyOverflow;
      document.body.style.paddingRight = state.bodyPaddingRight;
      if (!state.hadClass) document.documentElement.classList.remove("guru-nav-lock");
      this.scrollLockState = null;
    }

    clearHideTimer(name) {
      const timer = this.hideTimers.get(name);
      if (timer) window.clearTimeout(timer);
      this.hideTimers.delete(name);
    }

    setupCategoryNavigation() {
      const signal = this.listeners.signal;
      const drawer = this.root.querySelector('[data-guru-panel="menu"] .mobile-drawer');
      if (!drawer) return;
      const categories = [...drawer.querySelectorAll('[data-menu-category]')];
      window.matchMedia('(max-width:767px)').addEventListener('change', () => {
        categories.forEach(category => { category.open = false; });
        drawer.classList.remove('has-category');
      }, { signal });
      categories.forEach((category) => {
        category.addEventListener('toggle', () => {
          if (category.open) categories.forEach((other) => { if (other !== category) other.open = false; });
          drawer.classList.toggle('has-category', categories.some((item) => item.open));
          if (category.open && window.matchMedia('(max-width:767px)').matches) window.requestAnimationFrame(() => window.requestAnimationFrame(() => { if (category.open) category.querySelector('[data-menu-back]')?.focus({ preventScroll: true }); }));
        }, { signal });
        category.querySelector('summary')?.addEventListener('click', (event) => { event.preventDefault(); categories.forEach(other => { other.open = other === category; }); drawer.classList.add('has-category'); }, { signal });
        category.querySelector('summary')?.addEventListener('keydown', (event) => { if (event.key === 'ArrowRight') { event.preventDefault(); category.open = true; category.querySelector('.menu-category__links a')?.focus(); } }, { signal });
        category.querySelector('.menu-category__content')?.addEventListener('keydown', (event) => { if (event.key === 'ArrowLeft') { event.preventDefault(); category.open = false; drawer.classList.remove('has-category'); category.querySelector('summary')?.focus(); } }, { signal });
        category.querySelector('summary')?.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse' && window.matchMedia('(min-width:768px)').matches) category.open = true;
        }, { signal });
        category.querySelector('[data-menu-back]')?.addEventListener('click', () => {
          category.open = false;
          drawer.classList.remove('has-category');
          category.querySelector('summary')?.focus();
        }, { signal });
      });
    }

    setupPillarNavigation() {
      const signal = this.listeners.signal;
      this.root.querySelectorAll("[data-guru-pillar]").forEach((item) => {
        const trigger = item.querySelector("[data-guru-pillar-trigger]");
        const panel = item.querySelector("[data-guru-pillar-panel]");
        if (!trigger || !panel) return;

        let clickedUntilExit = false;
        item.addEventListener("pointermove", (event) => {
          if (!clickedUntilExit && event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
            this.openPillar(trigger.dataset.guruPillarTrigger);
          }
        }, { signal });

        trigger.addEventListener('click', () => {
          clickedUntilExit = true;
          this.close({ restoreFocus: false, immediate: true });
          this.closePillar({ immediate: true });
        }, { signal });
        trigger.addEventListener('keydown', (event) => { if (event.key === 'ArrowDown') { event.preventDefault(); this.openPillar(trigger.dataset.guruPillarTrigger); panel.querySelector('a')?.focus(); } }, { signal });
        item.addEventListener("pointerleave", (event) => { clickedUntilExit = false; if (event.pointerType === "mouse") this.schedulePillarClose(); }, { signal });

        item.addEventListener("focusout", (event) => {
          if (!item.contains(event.relatedTarget)) this.schedulePillarClose(0);
        }, { signal });
      });
    }

    openPillar(name) {

      const trigger = this.root.querySelector(`[data-guru-pillar-trigger="${name}"]`);
      const panel = this.root.querySelector(`[data-guru-pillar-panel="${name}"]`);
      if (!trigger || !panel) return false;

      window.clearTimeout(this.pillarCloseTimer);
      this.pillarCloseTimer = null;
      window.clearTimeout(this.pillarHideTimer);
      this.pillarHideTimer = null;
      if (this.activePillar === name) return true;

      [...instances.values()].forEach((instance) => {
        if (instance !== this) {
          instance.close({ restoreFocus: false, immediate: true });
          instance.closePillar({ immediate: true });
        }
      });

      this.close({ restoreFocus: false, immediate: true });
      this.closePillar({ immediate: true });
      this.hideNativeCart();
      this.closeAccountMenu();
      this.activePillar = name;
      panel.hidden = false;
      panel.setAttribute("aria-hidden", "false");
      trigger.setAttribute("aria-expanded", "true");
      this.root.classList.add("pillar-dropdown-open");
      this.updateGeometry();
      window.requestAnimationFrame(() => {
        if (this.activePillar === name) panel.classList.add("is-open");
      });
      return true;
    }

    schedulePillarClose(delay = 140) {
      window.clearTimeout(this.pillarCloseTimer);
      this.pillarCloseTimer = window.setTimeout(() => {
        this.pillarCloseTimer = null;
        this.closePillar();
      }, delay);
    }

    closePillar({ immediate = false, restoreFocus = false } = {}) {
      window.clearTimeout(this.pillarCloseTimer);
      this.pillarCloseTimer = null;
      if (!this.activePillar) return false;

      const name = this.activePillar;
      const trigger = this.root.querySelector(`[data-guru-pillar-trigger="${name}"]`);
      const panel = this.root.querySelector(`[data-guru-pillar-panel="${name}"]`);
      this.activePillar = null;
      trigger?.setAttribute("aria-expanded", "false");
      panel?.setAttribute("aria-hidden", "true");
      panel?.classList.remove("is-open");
      this.root.classList.remove("pillar-dropdown-open");

      if (panel) {
        const delay = immediate ? 0 : transitionDuration();
        const hidePanel = () => {
          if (this.activePillar !== name) panel.hidden = true;
          this.pillarHideTimer = null;
        };
        if (delay === 0) hidePanel();
        else this.pillarHideTimer = window.setTimeout(hidePanel, delay);
      }
      if (restoreFocus && isVisible(trigger)) trigger.focus({ preventScroll: true });
      return true;
    }

    setupSearch() {
      const panel = this.panels.get("search");
      const input = panel?.querySelector("[data-guru-search-input]");
      if (!panel || !input) return;

      const form = panel.querySelector("[data-guru-search-form]");
      const clearButton = panel.querySelector("[data-guru-search-clear]");
      const collectionLinks = panel.querySelector("[data-guru-search-links]");
      const initialCollectionLinks = [...collectionLinks.childNodes].map((node) => node.cloneNode(true));
      const results = panel.querySelector("[data-guru-search-results]");
      const recommendationTemplate = panel.querySelector("template[data-guru-search-recommendations]");
      const recommendationsHydrated = !recommendationTemplate;
      const initialResults = recommendationsHydrated ? [...results.childNodes] : [];
      const feedback = panel.querySelector("[data-guru-search-feedback]");
      const title = panel.querySelector("[data-guru-search-results-title]");
      const viewAll = panel.querySelector("[data-guru-search-view-all]");
      this.search = {
        panel,
        form,
        input,
        clearButton,
        products: panel.querySelector("[data-guru-search-products]"),
        results,
        initialResults,
        recommendationTemplate,
        recommendationsHydrated,
        feedback,
        initialFeedbackText: feedback.textContent,
        initialFeedbackHidden: feedback.hidden,
        status: panel.querySelector("[data-guru-search-status]"),
        title,
        initialTitle: title.textContent,
        sideTitle: panel.querySelector("[data-guru-search-side-title]"),
        initialSideTitle: panel.querySelector("[data-guru-search-side-title]")?.textContent || "",
        viewAll,
        initialViewAllHref: viewAll.getAttribute("href") || "",
        initialViewAllText: viewAll.textContent,
        initialViewAllHidden: viewAll.hidden,
        collectionLinks,
        initialCollectionLinks,
        chips: panel.querySelector(".search-chips"),
        discoveryTitle: panel.querySelector("[data-guru-search-discovery-title]"),
        initialChips: [...panel.querySelectorAll(".search-chips button")].map((node) => node.cloneNode(true)),
        popularTitle: panel.querySelector(".search-chips").getAttribute("aria-label"),
        suggestionLimit: Number(panel.querySelector(".search-chips").dataset.guruSearchSuggestionLimit) || 3,
        debounceMs: Number(
          panel.querySelector("[data-guru-search-debounce-ms]")?.dataset.guruSearchDebounceMs,
        ) || 180,
      };

      input.addEventListener("input", () => {
        this.invalidatePredictiveQuickBuy("input");
        const query = input.value.trim();
        clearButton.hidden = query.length === 0;
        window.clearTimeout(this.searchTimer);
        this.searchTimer = null;
        this.searchNeedsRefresh = false;
        this.searchSequence += 1;
        this.searchRequest?.abort();
        this.searchRequest = null;

        if (query.length < 2) {
          this.resetSearch();
          return;
        }

        this.setSearchLoading();
        this.searchTimer = window.setTimeout(() => {
          this.searchTimer = null;
          void this.fetchSearch(query);
        }, this.search.debounceMs);
      }, { signal: this.listeners.signal });

      input.addEventListener("change", () => {
        this.invalidatePredictiveQuickBuy("change");
      }, { signal: this.listeners.signal });

      form.addEventListener("submit", (event) => {
        if (!input.value.trim()) event.preventDefault();
        else this.invalidatePredictiveQuickBuy("navigation");
      }, { signal: this.listeners.signal });

      clearButton.addEventListener("click", () => {
        this.invalidatePredictiveQuickBuy("clear");
        input.value = "";
        input.focus();
        this.resetSearch({ invalidateQuickBuy: false });
      }, { signal: this.listeners.signal });

    }

    hydrateSearchRecommendations() {
      if (!this.search || this.search.recommendationsHydrated) return;

      const initialResults = [...this.search.recommendationTemplate.content.childNodes];
      this.search.initialResults = initialResults;
      this.search.recommendationsHydrated = true;
      this.search.results.dataset.guruSearchRecommendationsHydrated = "true";
      this.search.results.replaceChildren(...initialResults);
      this.search.recommendationTemplate.remove();
      this.search.recommendationTemplate = null;
    }

    invalidatePredictiveQuickBuy(reason) {
      if (!this.search?.panel) return;
      this.search.panel.dispatchEvent(new CustomEvent(PREDICTIVE_INVALIDATE_EVENT, {
        bubbles: true,
        detail: { root: this.search.panel, reason },
      }));
    }

    resetSearch({ invalidateQuickBuy = true } = {}) {
      if (!this.search) return;

      if (invalidateQuickBuy) this.invalidatePredictiveQuickBuy("reset");

      window.clearTimeout(this.searchTimer);
      this.searchTimer = null;
      this.searchNeedsRefresh = false;
      this.searchSequence += 1;
      this.searchRequest?.abort();
      this.searchRequest = null;
      this.search.clearButton.hidden = this.search.input.value.trim().length === 0;
      this.renderSuggestions([], this.search.input.value.trim());
      this.search.panel.dataset.searchState = "idle";
      this.search.products.setAttribute("aria-busy", "false");
      this.search.title.textContent = this.search.initialTitle;
      this.search.sideTitle.textContent = this.search.initialSideTitle;
      this.search.sideTitle.hidden = false;
      this.search.feedback.textContent = this.search.initialFeedbackText;
      this.search.feedback.hidden = this.search.initialFeedbackHidden;
      if (
        this.search.results.childNodes.length !== this.search.initialResults.length
        || this.search.initialResults.some((node, index) => this.search.results.childNodes[index] !== node)
      ) this.search.results.replaceChildren(...this.search.initialResults);
      this.search.viewAll.href = this.search.initialViewAllHref;
      this.search.viewAll.textContent = this.search.initialViewAllText;
      this.search.viewAll.hidden = this.search.initialViewAllHidden;
      this.search.status.textContent = "";
      this.search.collectionLinks.replaceChildren(
        ...this.search.initialCollectionLinks.map((node) => node.cloneNode(true)),
      );
    }

    async fetchSearch(query) {
      if (!this.search || query.length < 2) return;

      this.searchRequest?.abort();
      this.searchRequest = new AbortController();
      const requestSequence = ++this.searchSequence;
      this.setSearchLoading();

      try {
        const endpoint = new URL(
          this.root.dataset.predictiveSearchUrl || "/search/suggest",
          window.location.origin,
        );
        endpoint.searchParams.set("q", query);
        endpoint.searchParams.set("resources[type]", "product,query,collection");
        endpoint.searchParams.set("resources[limit]", "3");
        endpoint.searchParams.set("resources[limit_scope]", "each");
        endpoint.searchParams.set("resources[options][unavailable_products]", "hide");
        endpoint.searchParams.set("section_id", "predictive-search");

        const response = await fetch(endpoint, {
          credentials: "same-origin",
          headers: { Accept: "text/html" },
          signal: this.searchRequest.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed with ${response.status}`);
        if (requestSequence !== this.searchSequence) return;

        const responseHtml = await response.text();
        if (requestSequence !== this.searchSequence) return;
        const responseDocument = new DOMParser().parseFromString(responseHtml, "text/html");
        const fragment = responseDocument.querySelector("[data-guru-predictive-search-response]");
        const results = fragment?.querySelector("[data-guru-predictive-results]");
        const queryTemplate = fragment?.querySelector("template[data-guru-predictive-queries]");
        if (!fragment || !results || !(queryTemplate instanceof HTMLTemplateElement)) {
          throw new Error("Predictive search returned an invalid Guru fragment");
        }
        const normalizeTerm = (value) => String(value || "")
          .normalize("NFKC")
          .trim()
          .replace(/\s+/g, " ")
          .toLocaleLowerCase(this.root.dataset.language || undefined);
        if (normalizeTerm(fragment.dataset.guruPredictiveSearchTerm) !== normalizeTerm(query)) {
          throw new Error("Predictive search returned results for a different query");
        }
        const products = [...results.children];
        if (!products.every((product) => product.matches("product-card[data-guru-product-card]"))) {
          throw new Error("Predictive search returned a non-shared product card");
        }
        const queries = [...queryTemplate.content.querySelectorAll("[data-guru-predictive-query]")]
          .map((item) => ({ text: item.dataset.guruPredictiveQuery || "" }))
          .filter((item) => item.text);

        this.renderSuggestions(queries, query);
        this.renderProducts(products, query);
        const collections = fragment.querySelector("template[data-guru-predictive-collections]");
        const links = collections instanceof HTMLTemplateElement ? [...collections.content.children] : [];
        this.search.collectionLinks.replaceChildren(...links);
        this.search.sideTitle.hidden = links.length === 0;
      } catch (error) {
        if (error.name === "AbortError" || requestSequence !== this.searchSequence) return;
        this.setSearchFeedback(
          "error",
          this.translate("errorMessage"),
          this.translate("errorAnnouncement"),
        );
      } finally {
        if (requestSequence === this.searchSequence) this.searchRequest = null;
      }
    }

    setSearchLoading() {
      this.invalidatePredictiveQuickBuy("replacement");
      this.search.panel.dataset.searchState = "loading";
      this.search.products.setAttribute("aria-busy", "true");
      this.search.title.textContent = this.translate("loadingTitle");
      this.search.sideTitle.hidden = true;
      this.search.collectionLinks.replaceChildren();
      this.search.chips.replaceChildren();
      this.search.chips.hidden = true;
      if (this.search.discoveryTitle) this.search.discoveryTitle.hidden = true;
      this.search.feedback.hidden = true;
      this.search.viewAll.hidden = true;
      const skeletons = Array.from({ length: 3 }, () => {
        const skeleton = document.createElement("div");
        skeleton.className = "search-skeleton";
        skeleton.setAttribute("aria-hidden", "true");
        return skeleton;
      });
      this.search.results.replaceChildren(...skeletons);
      this.search.status.textContent = this.translate("loadingStatus");
    }

    setSearchFeedback(state, message, announcement, query = "") {
      this.search.panel.dataset.searchState = state;
      this.search.products.setAttribute("aria-busy", "false");
      this.search.title.textContent = query
        ? this.translate("resultsFor", { query })
        : this.translate("searchTitle");
      this.search.feedback.textContent = message;
      this.search.feedback.hidden = false;
      this.search.results.replaceChildren();
      this.search.viewAll.hidden = true;
      this.search.status.textContent = announcement;
      if (state === "error") {
        this.renderSuggestions([], this.search.input.value.trim());
        this.search.sideTitle.textContent = this.translate("collections");
        this.search.sideTitle.hidden = false;
        this.search.collectionLinks.replaceChildren(
          ...this.search.initialCollectionLinks.map((node) => node.cloneNode(true)),
        );
      }
    }

    renderSuggestions(queries, query) {
      const normalize = (value) => value.normalize("NFKC").trim().replace(/\s+/g, " ")
        .toLocaleLowerCase(this.root.dataset.language || undefined);
      const term = normalize(query);
      this.search.panel.dataset.searchQuery = String(Boolean(term));
      const seen = new Set();
      const suggestions = queries.map((item) => item.text?.trim()).filter((text) => {
        if (!text) return false;
        const key = normalize(text);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, this.search.suggestionLimit);
      const buttons = term && suggestions.length ? suggestions.map((text) => {
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.guruSearchQuery = text;
        button.textContent = sentenceCase(text, this.root.dataset.language);
        return button;
      }) : this.search.initialChips.filter((button) => !term
        || normalize(button.dataset.guruSearchQuery || "").startsWith(term))
        .map((button) => button.cloneNode(true));
      const label = term ? this.translate("suggestions") : this.search.popularTitle;
      this.search.chips.replaceChildren(...buttons);
      this.search.chips.hidden = buttons.length === 0;
      this.search.chips.setAttribute("aria-label", label);
      if (this.search.discoveryTitle) {
        this.search.discoveryTitle.textContent = label;
        this.search.discoveryTitle.hidden = buttons.length === 0;
      }
    }

    renderProducts(products, query) {
      this.invalidatePredictiveQuickBuy("replacement");
      this.search.products.setAttribute("aria-busy", "false");
      if (!products.length) {
        this.setSearchFeedback(
          "no-results",
          this.translate("noResultsMessage", { query }),
          this.translate("noResultsAnnouncement", { query }),
          query,
        );
        this.setSearchViewAll(query);
        return;
      }

      this.search.panel.dataset.searchState = "results";
      this.search.title.textContent = this.translate("resultsFor", { query });
      this.search.feedback.hidden = true;
      this.search.results.replaceChildren(...products);
      this.setSearchViewAll(query);
      this.search.status.textContent = this.translate(
        products.length === 1 ? "foundOne" : "foundOther",
        { count: products.length },
      );
    }

    setSearchViewAll(query) {
      const searchUrl = new URL(this.root.dataset.searchUrl || "/search", window.location.origin);
      searchUrl.searchParams.set("q", query);
      searchUrl.searchParams.set("type", "product");
      this.search.viewAll.href = searchUrl;
      this.search.viewAll.textContent = this.translate("viewAll");
      this.search.viewAll.hidden = false;
    }

    destroy() {
      this.invalidatePredictiveQuickBuy("section-unload");
      this.close({ restoreFocus: false, immediate: true });
      this.closePillar({ immediate: true });
      this.listeners.abort();
      this.releaseGeometryOwners();
      this.resizeObserver?.disconnect();
      this.backgroundObserver?.disconnect();
      this.cartCountObserver?.disconnect();
      window.cancelAnimationFrame(this.geometryFrame);
      this.geometryFrame = null;
      const searchShell = this.panels.get("search");
      this.root.style.removeProperty("--guru-navigation-overlay-top");
      this.root.style.removeProperty("--guru-navigation-header-bottom");
      this.root.removeAttribute("data-scroll-state");
      this.root.removeAttribute("data-mobile-header");
      this.root.removeAttribute("data-sticky-stack");
      this.stickyToolbar?.removeAttribute("data-guru-sticky-stack");
      this.stickyToolbar = null;
      searchShell?.style.removeProperty("top");
      searchShell?.querySelector(".search-panel")?.style.removeProperty("max-height");
      this.searchRequest?.abort();
      window.clearTimeout(this.searchTimer);
      window.clearTimeout(this.pillarCloseTimer);
      window.clearTimeout(this.pillarHideTimer);
      this.hideTimers.forEach((timer) => window.clearTimeout(timer));
      this.hideTimers.clear();
      this.setBackgroundInert(false);
      this.unlockScroll();
      instances.delete(this.root);
      const replacement = primaryInstance();
      if (replacement) replacement.updateGeometry();
      else document.documentElement.style.removeProperty("--header-height");
    }
  }

  function init(scope = document) {
    const roots = [];
    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll?.(ROOT_SELECTOR) || []);

    roots.forEach((root) => {
      if (!instances.has(root)) instances.set(root, new GuruGlobalNavigation(root));
    });
  }

  function destroyWithin(scope) {
    [...instances.entries()].forEach(([root, instance]) => {
      if (root === scope || scope.contains?.(root)) instance.destroy();
    });
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(EXTERNAL_MARKET_TRIGGER);
    if (!trigger || trigger.closest(ROOT_SELECTOR)) return;

    event.preventDefault();
    primaryInstance()?.open("market", trigger);
  });

  document.addEventListener(OPEN_EVENT, (event) => {
    const name = event.detail?.name;
    if (!name) return;
    primaryInstance()?.open(name, event.detail?.trigger || document.activeElement);
  });

  document.addEventListener(CLOSE_EVENT, (event) => {
    [...instances.values()].forEach((instance) => instance.close(event.detail));
  });

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
    const instance = primaryInstance();
    instance?.syncExternalMarketTriggers();
    instance?.syncAfterSectionLoad(event.target);
  });
  document.addEventListener("shopify:section:unload", (event) => {
    const instance = primaryInstance();
    if (
      instance
      && event.target !== instance.section
      && !event.target.contains?.(instance.root)
    ) instance.syncBeforeSectionUnload(event.target);
    destroyWithin(event.target);
  });

  const api = {
    init,
    open(name, trigger) {
      return primaryInstance()?.open(name, trigger) || false;
    },
    close(options) {
      return primaryInstance()?.close(options) || false;
    },
  };
  window.GuruGlobalNavigation = api;
  init(document);
  openRequestedCart();
})();
